import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ClipboardList, Search, Phone, Hammer } from "lucide-react";

const steps = [
  {
    num: "01",
    icon: ClipboardList,
    title: "Completa el formulario",
    description: "Cuéntanos sobre tu empresa y el proyecto que tienes en mente.",
  },
  {
    num: "02",
    icon: Search,
    title: "Evaluamos tu proyecto",
    description: "Nuestro equipo revisa tu información y valida si encaja con esta campaña especial.",
  },
  {
    num: "03",
    icon: Phone,
    title: "Sesión de diagnóstico",
    description: "Si tu proyecto aplica, agendamos una llamada para entender mejor tus objetivos y definir el alcance.",
  },
  {
    num: "04",
    icon: Hammer,
    title: "Desarrollo de tu solución",
    description: "Diseñamos y construimos el software acordado, alineado a tus procesos y necesidades.",
  },
];

const HowItWorksSection = ({ onApplyClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="como-funciona"
      data-testid="how-it-works-section"
      ref={ref}
      className="py-24 sm:py-32"
      style={{ backgroundColor: "#0A0A0A" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono tracking-[0.25em] uppercase text-neutral-500 block mb-4">
            Proceso
          </span>
          <h2
            data-testid="how-it-works-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            ¿Cómo funciona esta campaña de desarrollo gratis?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 * (i + 1) }}
              className="group relative bg-[#050505] border border-white/[0.06] hover:border-white/20 p-8 transition-all duration-300"
            >
              <span className="text-5xl font-black text-white/[0.04] absolute top-4 right-4" style={{ fontFamily: "'Syne', sans-serif" }}>
                {step.num}
              </span>
              <step.icon className="w-6 h-6 text-white mb-6 opacity-50 group-hover:opacity-100 transition-opacity" />
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-neutral-600 block mb-3">
                Paso {step.num}
              </span>
              <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>
                {step.title}
              </h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 border-l border-white/10 pl-6"
        >
          <p className="text-sm text-neutral-500 leading-relaxed max-w-2xl">
            Esta campaña es una oportunidad para que pruebes cómo trabajamos y veas el
            impacto de una solución de software a medida en tu operación.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 1 }}
          className="mt-12 flex flex-col items-center text-center"
        >
          <button
            data-testid="how-it-works-cta-btn"
            onClick={onApplyClick}
            className="bg-white text-black font-bold text-sm tracking-[0.15em] uppercase px-10 py-5 hover:bg-neutral-200 transition-all active:scale-95"
          >
            Aplicar al desarrollo gratis
          </button>
          <p className="text-xs text-neutral-500 mt-4 max-w-md">
            Revisaremos tu información y te contactaremos por correo para contarte
            los siguientes pasos. Usaremos tus datos solo para gestionar tu solicitud.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
